import React, { useCallback, useEffect, useState } from 'react';
import { View } from 'react-native';
import LinearGradient from 'utils/packages/linearGradient';
import { Spacer, rem, Switch } from 'design-system';
import Text from 'design-system/Text';
import TouchableBounce from 'utils/packages/TouchableBounce';
import RadioButton from 'design-system/RadioButton';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { Amplitude } from 'utils';
import Animated, { cancelAnimation, Easing, useAnimatedStyle, useSharedValue, withDelay, withRepeat, withSequence, withTiming, } from 'react-native-reanimated';
import { selectEligibleFreeTrialDays } from 'features/premium/selectors';
import { useAppSelector } from 'store/hooks';
import AnimatedShiningEffect from './AnimatedShiningEffect';
// ==================================================
// Persistent bounce animation params
// ==================================================
const START_BOUNCE_Y = rem(10);
const STOP_BOUNCE_Y = rem(0);
const BOUCE_SNAP_DURATION = 50;
const BOUNCE_DURATION = 900;
const REPEAT_DELAY = 2000;
export const PlanSelectionList = ({ data, selection, onSelectionChange, isOnboardingFlow, hideFreeTrialSwitch, alt, containerStyle, withBorder, withoutGradient, changeSwitchText, onPressIn, onPressOut, withPersistentBounce, withShiningEffect, enableFreeTrial: enableFreeTrialProp, onEnableFreeTrialChange, hideRadioBtn, priceDisplayMode, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const [enableFreeTrialInternal, setEnableFreeTrialInternal] = useState(false);
    const isControlled = enableFreeTrialProp !== undefined;
    const enableFreeTrial = isControlled
        ? enableFreeTrialProp
        : enableFreeTrialInternal;
    const toggleEnableFreeTrial = useCallback((value) => {
        if (isControlled) {
            onEnableFreeTrialChange?.(value);
        }
        else {
            setEnableFreeTrialInternal(value);
        }
        Amplitude.logEvent('Switched Trial Toggle', {
            enableFreeTrial: value,
            onboarding: isOnboardingFlow ? 'yes' : 'no',
        });
    }, [isControlled, onEnableFreeTrialChange, isOnboardingFlow]);
    // ==================================================
    // Persistent bounce animation
    // ==================================================
    const translateY = useSharedValue(STOP_BOUNCE_Y);
    const animatedStyle = useAnimatedStyle(() => ({
        transform: [{ translateY: translateY.value }],
    }));
    useEffect(() => {
        if (withPersistentBounce) {
            if (!enableFreeTrial) {
                translateY.value = withDelay(REPEAT_DELAY, withRepeat(withSequence(withTiming(START_BOUNCE_Y, {
                    duration: BOUCE_SNAP_DURATION,
                    easing: Easing.inOut(Easing.ease),
                }), withTiming(STOP_BOUNCE_Y, {
                    duration: BOUNCE_DURATION,
                    easing: Easing.elastic(4),
                }), withTiming(STOP_BOUNCE_Y, {
                    duration: REPEAT_DELAY,
                })), -1, false));
            }
            else {
                translateY.value = withTiming(STOP_BOUNCE_Y, {
                    duration: 500,
                    easing: Easing.inOut(Easing.ease),
                });
            }
        }
        return () => {
            cancelAnimation(translateY);
            translateY.value = STOP_BOUNCE_Y;
        };
    }, [translateY, withPersistentBounce, enableFreeTrial]);
    const eligibleFreeTrialDays = useAppSelector(selectEligibleFreeTrialDays);
    if (!data.length)
        return null;
    return (<View style={containerStyle}>
      {!hideFreeTrialSwitch && (<Animated.View style={[
                styles.freeTrialSwitchContainer,
                alt && styles.freeTrialSwitchContainerAltPadding,
                alt && styles.darkFreeTrialSwitchContainer,
                withPersistentBounce && animatedStyle,
            ]}>
          <Text {...(changeSwitchText ? { centered: false } : { centered: true })} Text-16 style={!!changeSwitchText && styles.changeSwitchText}>
            {/* eslint-disable-next-line no-nested-ternary */}
            {changeSwitchText && enableFreeTrial
                ? eligibleFreeTrialDays != null
                    ? `${eligibleFreeTrialDays}-day free trial enabled`
                    : 'Free trial enabled'
                : 'Not sure yet? Enable free trial'}
          </Text>
          <View onTouchStart={onPressIn} onTouchEnd={onPressOut}>
            <Switch value={enableFreeTrial} onValueChange={toggleEnableFreeTrial} alt={alt}/>
            {!!withShiningEffect && (<AnimatedShiningEffect animate={enableFreeTrial}/>)}
          </View>
        </Animated.View>)}
      <Spacer h={alt ? 12 : 21}/>
      {data.map(({ period, oldPrice, newPrice, monthlyPrice, weeklyPrice, discount, title, badgeLabel, }, index) => {
            const notSelected = index !== selection;
            const isWeekly = priceDisplayMode === 'weekly';
            const isClearMonthly = priceDisplayMode === 'monthly';
            const isAnnual = period === 'Annual';
            // The plan's own total: yearly plans show the year price, monthly
            // plans show the month price.
            const totalPrice = isAnnual ? newPrice : monthlyPrice;
            const totalPeriodSuffix = isAnnual ? '/yr' : '/mo';
            return (<TouchableBounce onPress={() => {
                    onSelectionChange(index);
                }} onPressIn={onPressIn} onPressOut={onPressOut} style={[
                    styles.touchableC1,
                    alt && styles.transparentTouchableC1,
                    // eslint-disable-next-line no-nested-ternary
                    index === selection
                        ? styles.selectedTouchable
                        : withBorder
                            ? styles.withBorder
                            : null,
                ]} key={title ?? period}>
              {discount || badgeLabel ? (<LinearGradient style={styles.discountC1} angle={90} useAngle colors={index === selection
                        ? [
                            colors.gradients.ultimate.colors[0],
                            withoutGradient
                                ? colors.gradients.ultimate.colors[0]
                                : colors.gradients.pro.colors[0],
                        ]
                        : [
                            colors.elements.secondaryLight,
                            colors.elements.secondaryLight,
                        ]}>
                  <Text Text-14 OnPrimary>
                    {badgeLabel ?? `SAVE ${discount}%`}
                  </Text>
                </LinearGradient>) : null}
              <View style={[
                    styles.touchableContentContainer,
                    discount || badgeLabel
                        ? styles.touchableContentContainerWithDiscount
                        : undefined,
                ]}>
                <View style={styles.flex}>
                  <View style={styles.periodContainer}>
                    <Text {...(notSelected && { Secondary: true })} {...(isAnnual && !priceDisplayMode && !title
                ? { 'Text-20': true }
                : { 'Text-16': true })}>
                      {title ??
                    // eslint-disable-next-line no-nested-ternary
                    (isAnnual
                        ? priceDisplayMode
                            ? 'Yearly'
                            : 'Yearly plan'
                        : period)}
                    </Text>
                  </View>
                  {!priceDisplayMode && isAnnual && newPrice ? (<View style={[
                        styles.yearlyPriceContainer,
                        !oldPrice && styles.noMarginTop,
                    ]}>
                      <Text {...(oldPrice
                    ? { 'Numbers-16': true }
                    : { 'Numbers-14': true })} Secondary>
                        <Text {...(oldPrice
                    ? { 'TextThin-16': true }
                    : { 'Text-14': true })} Secondary>
                          12 mo
                        </Text>
                        ・
                        {oldPrice ? (<>
                            <Text Numbers-16 Secondary style={styles.oldPrice}>
                              {oldPrice}
                            </Text>{' '}
                          </>) : null}
                        <Text {...(oldPrice
                    ? { 'Numbers-16': true }
                    : { 'Numbers-14': true })} {...((notSelected || !oldPrice) && {
                    Secondary: true,
                })}>
                          {newPrice}
                        </Text>
                      </Text>
                    </View>) : null}
                  {isWeekly ? (<Text Numbers-16 {...(notSelected && { Secondary: true })}>
                      {totalPrice}
                      <Text TextThin-14 Secondary>
                        {totalPeriodSuffix}
                      </Text>
                    </Text>) : null}
                </View>
                {/* eslint-disable-next-line no-nested-ternary */}
                {isWeekly ? (<Text Numbers-16 right {...(notSelected && { Secondary: true })}>
                    {weeklyPrice}
                    <Text TextThin-16 Secondary>
                      /wk
                    </Text>
                  </Text>) : isClearMonthly ? (<View style={styles.clearMonthlyRight}>
                    <Text Numbers-16 right {...(notSelected && { Secondary: true })}>
                      {totalPrice}
                      <Text TextThin-16 Secondary>
                        {totalPeriodSuffix}
                      </Text>
                    </Text>
                    {isAnnual ? (<Text Numbers-14 Secondary right>
                        {monthlyPrice}
                        <Text TextThin-14 Secondary>
                          /mo, renews annually
                        </Text>
                      </Text>) : null}
                  </View>) : (<Text Numbers-16 right {...(notSelected && { Secondary: true })}>
                    {monthlyPrice}
                    <Text TextThin-14 Secondary>
                      /mo
                    </Text>
                  </Text>)}
                {!hideRadioBtn && (<>
                    <Spacer w={10}/>
                    <RadioButton type="single" isChecked={index === selection}/>
                  </>)}
              </View>
            </TouchableBounce>);
        })}
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    touchableC1: {
        marginBottom: rem(8),
        borderRadius: rem(16),
        overflow: 'hidden',
        backgroundColor: colors.cards.onDark,
        borderWidth: 0,
    },
    transparentTouchableC1: {
        backgroundColor: colors.background.transparentDark,
    },
    withBorder: {
        borderWidth: rem(1),
        borderColor: colors.elements.secondaryLightTransparent,
    },
    selectedTouchable: {
        borderWidth: rem(1),
        borderColor: colors.elements.brand,
    },
    touchableContentContainer: {
        paddingHorizontal: rem(16),
        paddingVertical: rem(16),
        flexDirection: 'row',
        alignItems: 'center',
    },
    touchableContentContainerWithDiscount: {
        paddingTop: rem(12),
    },
    discountC1: {
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: rem(4),
    },
    flex: {
        flex: 1,
    },
    periodContainer: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    yearlyPriceContainer: {
        marginTop: rem(4),
        flexDirection: 'row',
        alignItems: 'center',
    },
    noMarginTop: {
        marginTop: 0,
    },
    clearMonthlyRight: {
        alignItems: 'flex-end',
    },
    oldPrice: {
        textDecorationLine: 'line-through',
    },
    freeTrialSwitchContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: rem(16),
        backgroundColor: colors.background.brandLight,
        borderRadius: 999,
    },
    freeTrialSwitchContainerAltPadding: {
        paddingVertical: rem(12),
    },
    darkFreeTrialSwitchContainer: {
        backgroundColor: '#17102AD9',
    },
    changeSwitchText: {
        flex: 1,
    },
}));
